import { ProjectState } from './project-state';
import {
  AgreementCalculationType,
  AgreementInterpretation,
  AgreementThresholdStatus,
} from './screening-agreement';

export type DataExtractionAgreementMetricType =
  | 'UNWEIGHTED_COHEN_KAPPA'
  | 'EXACT_SET_PERCENT_AGREEMENT'
  | 'NUMERIC_DIFFERENCE'
  | 'TEXT_EXACT_MATCH';

export type DataExtractionAgreementReason =
  | 'NO_OVERLAPPING_DECISIONS'
  | 'INSUFFICIENT_DATA'
  | 'NO_CATEGORY_VARIATION'
  | 'NOT_APPLICABLE';

export type DataExtractionQuestionType =
  | 'FULL_TEXT'
  | 'SINGLE_CHOICE'
  | 'MULTI_CHOICE'
  | 'NUMERIC'
  | 'BOOLEAN';

export interface DataExtractionAgreementPair {
  firstReviewerId: number;
  secondReviewerId: number;
  firstReviewerName: string;
  secondReviewerName: string;
  pairedAnswerCount: number;
  agreementCount: number;
  disagreementCount: number;
  percentAgreement: number | null;
  cohenKappa: number | null;
  interpretation: AgreementInterpretation;
  thresholdStatus?: AgreementThresholdStatus | null;
}

export interface DataExtractionAgreementQuestion {
  questionId: number;
  questionText: string | null;
  questionType: DataExtractionQuestionType;
  metricType: DataExtractionAgreementMetricType;
  pairedAnswerCount: number;
  unpairedMissingCount: number;
  agreementCount: number;
  percentAgreement: number | null;
  cohenKappa: number | null;
  interpretation: AgreementInterpretation | null;
  nonComputableReason: DataExtractionAgreementReason | null;
  configuredThreshold: number | null;
  thresholdStatus: AgreementThresholdStatus | null;
  meanAbsoluteDifference: number | null;
  medianAbsoluteDifference: number | null;
  reviewerPairs: DataExtractionAgreementPair[];
}

export interface DataExtractionAgreementBase {
  calculationType: AgreementCalculationType;
  stage: ProjectState.DATA_EXTRACTION;
  totalExtractionQuestions: number;
  totalPairedAnswers: number;
  overallExactAgreementPercentage: number | null;
  questions: DataExtractionAgreementQuestion[];
}

export interface DataExtractionAgreementPreview extends DataExtractionAgreementBase {
  calculationType: 'PRELIMINARY';
}

export interface DataExtractionAgreementOfficial extends DataExtractionAgreementBase {
  calculationType: 'OFFICIAL';
  snapshotVersion: number;
  finalizedAt: string;
  finalizedByUserId: number;
  finalizedByUserName: string;
  calculationAlgorithmVersion: string;
}

export type DataExtractionAgreementResult =
  | DataExtractionAgreementPreview
  | DataExtractionAgreementOfficial;

export const DATA_EXTRACTION_METRIC_LABELS: Record<DataExtractionAgreementMetricType, string> = {
  UNWEIGHTED_COHEN_KAPPA: "Cohen's kappa",
  EXACT_SET_PERCENT_AGREEMENT: 'Exact-set agreement',
  NUMERIC_DIFFERENCE: 'Numeric difference',
  TEXT_EXACT_MATCH: 'Normalized exact match',
};

export const DATA_EXTRACTION_REASON_LABELS: Record<DataExtractionAgreementReason, string> = {
  NO_OVERLAPPING_DECISIONS: 'No overlapping reviewer answers are available yet.',
  INSUFFICIENT_DATA: "At least two paired answers are needed for Cohen's kappa.",
  NO_CATEGORY_VARIATION:
    "Cohen's kappa cannot be calculated because all paired answers used the same category.",
  NOT_APPLICABLE: "Cohen's kappa is not applicable for this question type.",
};

export const DATA_EXTRACTION_QUESTION_TYPE_LABELS: Record<DataExtractionQuestionType, string> = {
  BOOLEAN: 'Boolean',
  SINGLE_CHOICE: 'Single choice',
  MULTI_CHOICE: 'Multi-choice',
  NUMERIC: 'Numeric',
  FULL_TEXT: 'Free text',
};

export function dataExtractionMetricLabel(value: DataExtractionAgreementMetricType): string {
  return DATA_EXTRACTION_METRIC_LABELS[value] ?? value;
}

export function dataExtractionReasonLabel(value?: DataExtractionAgreementReason | null): string {
  return value ? DATA_EXTRACTION_REASON_LABELS[value] : '';
}

export function dataExtractionQuestionTypeLabel(value: DataExtractionQuestionType): string {
  return DATA_EXTRACTION_QUESTION_TYPE_LABELS[value] ?? value;
}
